/**
 * 👁️ First Look Badge Component
 * Shows exclusive early access status
 */

import React from 'react';

const FirstLookBadge = ({ 
  isActive, 
  expiresAt, 
  buyerCount,
  isPublic,
  hasAccess,
  showCountdown = true 
}) => {
  // Calculate remaining time
  const getRemainingTime = () => {
    if (!expiresAt) return null;
    const now = new Date();
    const exp = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    const diff = exp - now;
    
    if (diff <= 0) return { expired: true, text: 'Lejárt' };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return {
      expired: false,
      text: `${hours}h ${minutes}m`,
      urgent: hours < 12
    };
  };

  const remaining = showCountdown ? getRemainingTime() : null;

  // Not active - don't show
  if (!isActive && !isPublic) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-700 text-gray-400 text-xs">
        <span>⏳</span>
        <span>Hamarosan</span>
      </div>
    );
  }

  // Public after First Look
  if (isPublic) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-600/20 border border-green-500/30 text-green-400 text-xs font-semibold">
        <span>🌍</span>
        <span>Publikus</span>
      </div>
    );
  }

  // Has exclusive access
  if (hasAccess) {
    return (
      <div className="relative group">
        <div className={`
          inline-flex items-center gap-2 px-4 py-2 rounded-full
          bg-gradient-to-r from-purple-600 to-pink-600
          text-white font-bold text-sm shadow-lg shadow-purple-500/30
          ${remaining?.urgent ? 'animate-pulse' : ''}
        `}>
          <span className="text-lg">👁️</span>
          <span>EXKLUZÍV HOZZÁFÉRÉS</span>
          {remaining && !remaining.expired && (
            <span className={`
              ml-2 px-2 py-0.5 rounded-full text-xs
              ${remaining.urgent ? 'bg-red-500' : 'bg-white/20'}
            `}>
              {remaining.text}
            </span>
          )}
        </div>

        {/* Tooltip */}
        <div className="absolute top-full left-0 mt-2 hidden group-hover:block z-50 w-64">
          <div className="bg-black/95 backdrop-blur-xl border border-purple-500/30 rounded-xl p-4 shadow-2xl">
            <div className="text-sm text-white font-bold mb-2">
              🔥 First Look Privilégium
            </div>
            <ul className="text-xs text-gray-300 space-y-1.5">
              <li>• Te vagy az első {buyerCount || 'néhány'} befektető</li>
              <li>• 24-48 óra előny a többiekkel szemben</li>
              <li>• Közvetlen kapcsolat az eladóval</li>
              <li>• Még nincs verseny</li>
            </ul>
            {remaining && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className={remaining.urgent ? 'text-red-400' : 'text-yellow-400'}>
                  ⏰ {remaining.text} van hátra!
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No access - show request button style
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-700 border border-gray-600 text-gray-400 text-xs">
      <span>🔒</span>
      <span>Exkluzív még {remaining?.text || '48h'}</span>
    </div>
  );
};

export default FirstLookBadge;
